import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Car, FileText, Calculator, TrendingDown, Route } from 'lucide-react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';

const ServiceCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [windowWidth, setWindowWidth] = useState(0);
  const carouselRef = useRef(null);
  const startX = useRef(null);
  const currentX = useRef(null);
  const isDragging = useRef(false);

  useEffect(() => {
    setWindowWidth(window.innerWidth);
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const services = [
    {
      title: "Unfall- & Schadengutachten",
      description: "Unabhängige Bewertung von Fahrzeugschäden nach Unfällen",
      icon: Car,
      benefits: ["Schnelle Besichtigung vor Ort", "Anerkannt bei Versicherern", "Objektive Bewertung"],
      color: "#0078B5"
    },
    {
      title: "Wertgutachten",
      description: "Ermittlung des aktuellen Fahrzeugwerts für Verkauf oder Versicherung",
      icon: FileText,
      benefits: ["Marktgerechte Bewertung", "Für Versicherung & Verkauf", "Detaillierte Dokumentation"],
      color: "#02B4D8"
    },
    {
      title: "Kostenvoranschlag-Prüfung",
      description: "Überprüfung von Reparaturkosten auf Angemessenheit und Notwendigkeit",
      icon: Calculator,
      benefits: ["Kostentransparenz", "Vermeidung überhöhter Kosten", "Fachgerechte Bewertung"],
      color: "#0078B5"
    },
    {
      title: "Restwertermittlung",
      description: "Bestimmung des Restwerts nach Totalschaden für maximale Entschädigung",
      icon: TrendingDown,
      benefits: ["Maximale Entschädigung", "Fundierte Argumentation", "Schnelle Abwicklung"],
      color: "#02B4D8"
    },
    {
      title: "Unfallrekonstruktion",
      description: "Technische Analyse des Unfallhergangs für Haftungsklärung",
      icon: Route,
      benefits: ["Wissenschaftlich fundiert", "Für Haftungsklärung", "Gerichtsverwertbar"],
      color: "#0078B5"
    }
  ];

  const handlePrevious = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev === 0 ? services.length - 1 : prev - 1));
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev === services.length - 1 ? 0 : prev + 1));
    setTimeout(() => setIsAnimating(false), 300);
  };

  // Touch event handlers for mobile swipe
  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    isDragging.current = true;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    currentX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!isDragging.current || !startX.current || !currentX.current) return;
    
    const diffX = startX.current - currentX.current;
    const minSwipeDistance = 50;

    if (Math.abs(diffX) > minSwipeDistance) {
      if (diffX > 0) {
        handleNext(); // Swipe left -> next
      } else {
        handlePrevious(); // Swipe right -> previous
      }
    }

    startX.current = null;
    currentX.current = null;
    isDragging.current = false;
  };

  // Mouse event handlers for desktop drag
  const handleMouseDown = (e) => {
    startX.current = e.clientX;
    isDragging.current = true;
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    currentX.current = e.clientX;
  };

  const handleMouseUp = () => {
    if (!isDragging.current || !startX.current || !currentX.current) return;
    
    const diffX = startX.current - currentX.current;
    const minSwipeDistance = 50;

    if (Math.abs(diffX) > minSwipeDistance) {
      if (diffX > 0) {
        handleNext();
      } else {
        handlePrevious();
      }
    }

    startX.current = null;
    currentX.current = null;
    isDragging.current = false;
  };

  useEffect(() => {
    const handleMouseUpWindow = () => {
      if (isDragging.current) {
        handleMouseUp();
      }
    };

    window.addEventListener('mouseup', handleMouseUpWindow);
    return () => window.removeEventListener('mouseup', handleMouseUpWindow);
  }, []);

  const getVisibleIndexes = () => {
    const indexes = [];
    for (let i = -2; i <= 2; i++) {
      let index = (currentIndex + i + services.length) % services.length;
      indexes.push(index);
    }
    return indexes;
  };

  const getCardStyle = (index, position) => {
    const isCurrent = position === 0;
    const isAdjacent = Math.abs(position) === 1;
    const isFarther = Math.abs(position) === 2;

    // Responsive Werte - gleiche Optik, proportional skaliert
    const boxWidth = windowWidth >= 768 ? 448 : 320; // Desktop: 28rem, Mobile: 20rem (proportional kleiner)
    const peekPercentage = windowWidth >= 768 ? 0.035 : 0.03; // Desktop: 3,5%, Mobile: 3% für präzisen Streifen-Effekt
    const spacing = boxWidth * (1 - peekPercentage); // Proportionaler Abstand

    // Responsive Positionierung mit gleicher Optik
    const transform = `translateX(${position * spacing}px) scale(${isCurrent ? 1 : isAdjacent ? 0.7 : 0.4})`;

    return {
      transform: transform,
      opacity: isCurrent ? 1 : isAdjacent ? 0.4 : isFarther ? 0.15 : 0,
      zIndex: isCurrent ? 30 : isAdjacent ? 18 : 10,
      transition: isAnimating ? 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
    };
  };

  return (
    <div className="relative w-full overflow-hidden">
      {/* Navigation Arrows - 1:1 Identical Desktop/Mobile Design */}
      <Button
        onClick={handlePrevious}
        disabled={isAnimating}
        variant="outline"
        size="icon"
        className="absolute left-0 md:left-12 top-1/2 transform -translate-y-1/2 z-50 bg-[#0078B5] hover:bg-[#005A8A] border-2 border-white/20 hover:border-white/40 flex w-10 h-10 md:w-14 md:h-14 rounded-2xl transition-all duration-200"
        style={{
          boxShadow: '0 10px 25px -5px rgba(0, 120, 181, 0.15), 0 4px 6px -2px rgba(0, 120, 181, 0.05)'
        }}
      >
        <ChevronLeft className="h-5 w-5 md:h-8 md:w-8 text-white transition-transform" />
      </Button>

      <Button
        onClick={handleNext}
        disabled={isAnimating}
        variant="outline"
        size="icon"
        className="absolute right-0 md:right-12 top-1/2 transform -translate-y-1/2 z-50 bg-[#0078B5] hover:bg-[#005A8A] border-2 border-white/20 hover:border-white/40 flex w-10 h-10 md:w-14 md:h-14 rounded-2xl transition-all duration-200"
        style={{
          boxShadow: '0 10px 25px -5px rgba(0, 120, 181, 0.15), 0 4px 6px -2px rgba(0, 120, 181, 0.05)'
        }}
      >
        <ChevronRight className="h-5 w-5 md:h-8 md:w-8 text-white transition-transform" />
      </Button>

      {/* Carousel Container */}
      <div
        ref={carouselRef}
        className="relative h-[400px] md:h-[580px] flex items-center justify-center cursor-grab active:cursor-grabbing px-16 md:px-20"
        style={{ 
          overflow: 'visible', 
          paddingBottom: '50px', 
          marginBottom: '-50px',
          paddingTop: '20px',
          marginTop: '-20px'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
      >
        {getVisibleIndexes().map((serviceIndex, i) => {
          const service = services[serviceIndex];
          const position = i - 2; // -2, -1, 0, 1, 2
          const IconComponent = service.icon;

          return (
            <Card
              key={`${serviceIndex}-${currentIndex}`}
              className="absolute w-80 h-80 md:w-[28rem] md:h-[28rem] flex flex-col justify-center hover:shadow-2xl transition-all duration-500 bg-white"
              style={{
                ...getCardStyle(serviceIndex, position), 
                aspectRatio: '1/1',
                borderRadius: '2rem',
                // Responsiver dünner schwarzer Rand (proportional skaliert)
                border: '1px solid #000000',
                // Responsive schwarze/dunkelgraue Schatten (proportional skaliert für Mobile/Desktop)
                boxShadow: position === 0 ? (
                  // Aktive Box - Schatten proportional zur Bildschirmgröße
                  windowWidth >= 768 
                    ? '0 30px 60px -15px rgba(0, 0, 0, 0.25), 0 -15px 30px -8px rgba(0, 0, 0, 0.12), 15px 0 30px -8px rgba(0, 0, 0, 0.12), -15px 0 30px -8px rgba(0, 0, 0, 0.12)' // Desktop
                    : '0 20px 40px -10px rgba(0, 0, 0, 0.25), 0 -10px 20px -5px rgba(0, 0, 0, 0.12), 10px 0 20px -5px rgba(0, 0, 0, 0.12), -10px 0 20px -5px rgba(0, 0, 0, 0.12)' // Mobile (proportional kleiner)
                ) : Math.abs(position) === 1 ? (
                  // Nachbar-Streifen - kleinere aber sichtbare Schatten für erkennbare Ränder
                  windowWidth >= 768
                    ? '0 15px 30px -8px rgba(0, 0, 0, 0.15), 0 -8px 15px -4px rgba(0, 0, 0, 0.08), 8px 0 15px -4px rgba(0, 0, 0, 0.08), -8px 0 15px -4px rgba(0, 0, 0, 0.08)' // Desktop
                    : '0 8px 16px -4px rgba(0, 0, 0, 0.18), 0 -4px 8px -2px rgba(0, 0, 0, 0.1), 4px 0 8px -2px rgba(0, 0, 0, 0.1), -4px 0 8px -2px rgba(0, 0, 0, 0.1)' // Mobile: stärkere Schatten für schmale Streifen
                ) : 'none'
              }}
            >
              <CardHeader className="text-center pb-4 md:pb-6 px-4 md:px-8">
                <div className="flex flex-col items-center mb-4 md:mb-6">
                  <div className="p-3 md:p-5 bg-[#93E2F1] rounded-2xl md:rounded-3xl mb-3 md:mb-6 shadow-md">
                    <IconComponent size={windowWidth >= 768 ? 48 : 36} style={{ color: service.color }} />
                  </div>
                  <CardTitle className="text-lg md:text-2xl lg:text-3xl font-bold leading-tight text-center px-2 md:px-3" style={{ color: '#0078B5' }}>
                    {service.title}
                  </CardTitle>
                </div>
                <CardDescription className="text-gray-600 text-sm md:text-base lg:text-lg leading-relaxed text-center px-2 md:px-3">
                  {service.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="px-4 md:px-8 pb-4 md:pb-8">
                <ul className="space-y-2 md:space-y-3">
                  {service.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-center space-x-3 md:space-x-4 text-sm md:text-base lg:text-lg text-gray-700">
                      <div className="w-2 h-2 md:w-3 md:h-3 bg-[#02B4D8] rounded-full flex-shrink-0"></div>
                      <span className="leading-normal font-medium">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Mobile Swipe Hint - REMOVED */}
    </div>
  );
};

export default ServiceCarousel;